import * as React from 'react';
import {
  Dialog,
  DialogContent,
  Grid,
  Button,
  Typography,
  TextField,
} from '@material-ui/core';
import axios from 'axios';
import useSWR from 'swr';
import Autocomplete from '@material-ui/lab/Autocomplete';
import type { IRepairItem } from '../marking/MarkingInterfaces';

interface MetalBinEmptyDialogProps {
  onClose: () => void;
  emptyBin: string;
  emptyBinItem: IRepairItem;
  sendNotification: (messageType: 'Empty Bin' | 'Low Stock', message: string) => void
}
const MetalBinEmptyDialog: React.FC<MetalBinEmptyDialogProps> = ({
  onClose, emptyBin, emptyBinItem, sendNotification,
}) => {
  const [bin, setBin] = React.useState(emptyBin);

  const binFetcher = (url: string, repairItemParam: IRepairItem) => axios.get<{ metalBins: string[] }>(url, {
    params: {
      metalType: repairItemParam.metalType.label,
      ringWidthParameter: repairItemParam.width,
      ringThicknessParameter: repairItemParam.thickness,
      changeInSizeParameter: repairItemParam.sizeChange,
    },
  }).then((res) => res.data);

  const { data } = useSWR(emptyBinItem ? ['/MetalCollection/GetMetalBinReplacement', emptyBinItem] : null, (url, repairItemParam) => binFetcher(url, repairItemParam));

  if (emptyBin === undefined) {
    return null;
  }

  return (
    <Dialog
      onClose={onClose}
      open={Boolean(emptyBin)}
      fullWidth
      maxWidth="sm"
    >
      <DialogContent>
        <Grid container direction="row-reverse" spacing={1}>
          <Grid item xs={12}>
            <Typography variant="overline">
              {`BIN ${emptyBin} IS EMPTY`}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            <Typography variant="h5" color="primary">
              {'Choose a new bin to pull from and record it below.'}
            </Typography>
          </Grid>
          <Grid item xs={12}>
            {
              data
                ? (
                  <Autocomplete
                    style={{ paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
                    value={bin}
                    onChange={(event: unknown, selectedBin: string | null) => {
                      if (selectedBin) setBin(selectedBin);
                    }}
                    id="new-bin-input"
                    autoHighlight
                    autoSelect
                    options={data?.metalBins?.filter((value) => value !== emptyBin) || []}
                // prop spreading is directly from MUI tsx example
                // eslint-disable-next-line react/jsx-props-no-spreading
                    renderInput={(params) => <TextField {...params} label="New Bin" variant="outlined" />}
                  />
                )
                : (
                  <TextField
                    style={{ paddingTop: '0.75rem', paddingBottom: '0.75rem' }}
                    disabled
                  />
                )
              }
          </Grid>
          <Grid item xs={12}>
            <Typography style={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.75rem' }} variant="subtitle1">
              {`After confirming the new bin you’re pulling from, the production manager will receive a notification that Bin ${emptyBin} is empty. No further action from you is needed.`}
            </Typography>
          </Grid>
          <Grid item>
            <Button
              id="confirm-new-bin-button"
              color="primary"
              variant="contained"
              onClick={() => { if (bin) sendNotification('Empty Bin', bin); }}
            >
              {'Confirm New Bin'}
            </Button>
          </Grid>
          <Grid item>
            <Button
              id="cancel-empty-modal-button"
              color="primary"
              onClick={onClose}
            >
              {'Cancel'}
            </Button>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};

export default MetalBinEmptyDialog;
